import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Select,
  Spinner,
  Tag,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import {
  AddOfferCurrenciesRequest,
  OfferGroupBranchCurrency,
  offerApi,
} from '@texas/api/endpoints/offerApi';
import { currenciesApi } from '@texas/api/endpoints/currenciesApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';
import { exchangeRateCalculation } from '@texas/utils/helpers/numberHelpers';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  offerGroupId: number;
  branchId: number;
  branchName: string;
  customerId: number;
  existingCurrencies: OfferGroupBranchCurrency[];
  onAdded: () => void;
}

export function AddOfferCurrencyDrawer({
  offerGroupId,
  branchId,
  branchName,
  customerId,
  existingCurrencies,
  onAdded,
}: Props) {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    useCurrenciesData: { findCurrency },
  } = useMetadata();

  const {
    data: allCurrencies,
    loading: currenciesLoading,
    refetch: refetchCurrencies,
  } = useApiResource(
    currenciesApi.getAllCurrenciesMergedWithCustomerCurrencies,
  );

  const [selectedCurrencyIds, setSelectedCurrencyIds] = useState<number[]>([]);
  const [convertFromCurrencyId, setConvertFromCurrencyId] = useState<
    number | null
  >(null);

  const { request: addRequest, loading: adding } = useApiRequest(
    offerApi.addCurrencies,
  );

  const handleOpen = () => {
    setSelectedCurrencyIds([]);
    setConvertFromCurrencyId(null);
    refetchCurrencies(customerId);
    onOpen();
  };

  const availableCurrencies = (allCurrencies ?? []).filter(
    (c) =>
      c.branchId === branchId &&
      !existingCurrencies.some((currency) => currency.currencyId === c.id),
  );
  const selectedCurrencies = (allCurrencies ?? []).filter((currency) =>
    selectedCurrencyIds.includes(currency.id),
  );
  const convertFromCurrency = existingCurrencies.find(
    (currency) => currency.currencyId === convertFromCurrencyId,
  );
  const sourceOffers = convertFromCurrency
    ? convertFromCurrency.childOffers.length > 0
      ? convertFromCurrency.childOffers
      : convertFromCurrency.masterOffer
      ? [convertFromCurrency.masterOffer]
      : []
    : [];
  const sourceRate = convertFromCurrency?.masterOffer?.exchangeRate ?? null;
  const rowLabel = (offer: typeof sourceOffers[number]) => {
    if (!offer.matrixNodeValueXName && !offer.matrixNodeValueYName) {
      return t('offer.offer');
    }
    if (offer.matrixNodeValueXName && offer.matrixNodeValueYName) {
      return `${offer.matrixNodeValueXName} / ${offer.matrixNodeValueYName}`;
    }
    return offer.matrixNodeValueXName ?? offer.matrixNodeValueYName ?? '—';
  };
  const previewRows =
    sourceRate == null
      ? []
      : selectedCurrencies.flatMap((targetCurrency) =>
          sourceOffers.flatMap((offer) =>
            offer.tierPrices.map((tierPrice) => ({
              key: `${targetCurrency.id}-${offer.id}-${tierPrice.id}`,
              currencyCode: targetCurrency.code,
              rowLabel: rowLabel(offer),
              quantity: tierPrice.quantity,
              price:
                tierPrice.price != null
                  ? exchangeRateCalculation(
                      tierPrice.price,
                      sourceRate,
                      targetCurrency.sellingRate,
                      5,
                    )
                  : null,
            })),
          ),
        );

  const toggleCurrency = (id: number) => {
    setSelectedCurrencyIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleAdd = async () => {
    const req: AddOfferCurrenciesRequest = {
      branchId,
      currencyIds: selectedCurrencyIds,
      convertFromCurrencyId,
    };
    await addRequest(offerGroupId, req);
    onClose();
    onAdded();
  };

  return (
    <>
      <Button variant="texas-light" size="sm" onClick={handleOpen}>
        {t('variant.addCurrency')}
      </Button>

      <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="sm">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>
            {t('variant.addCurrency')}
            <Text fontSize="sm" fontWeight="normal" color="gray.400" mt={1}>
              {branchName}
            </Text>
          </DrawerHeader>
          <DrawerBody>
            {currenciesLoading ? (
              <Spinner size="sm" />
            ) : (
              <Flex direction="column" gap={2}>
                {availableCurrencies.map((c) => {
                  const selected = selectedCurrencyIds.includes(c.id);
                  return (
                    <Button
                      key={c.id}
                      variant={selected ? 'texas-solid' : 'texas-light'}
                      size="sm"
                      justifyContent="flex-start"
                      onClick={() => toggleCurrency(c.id)}
                    >
                      <Tag mr={2} size="sm">
                        {c.code}
                      </Tag>
                      <Text fontSize="sm">{c.name}</Text>
                    </Button>
                  );
                })}
              </Flex>
            )}

            {existingCurrencies.length > 0 && (
              <Box mt={4}>
                <Text fontSize="sm" mb={1} color="gray.400">
                  {t('offer.convertFrom')}
                </Text>
                <Select
                  size="sm"
                  placeholder="-"
                  value={convertFromCurrencyId ?? ''}
                  onChange={(e) =>
                    setConvertFromCurrencyId(
                      e.target.value ? Number(e.target.value) : null,
                    )
                  }
                >
                  {existingCurrencies.map((currency) => (
                    <option
                      key={currency.currencyId}
                      value={currency.currencyId}
                    >
                      {findCurrency(currency.currencyId)?.code ??
                        String(currency.currencyId)}
                    </option>
                  ))}
                </Select>
                {previewRows.length > 0 && (
                  <TableContainer mt={3}>
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th>{t('general.currency')}</Th>
                          <Th>{t('offer.offer')}</Th>
                          <Th>{t('general.quantity')}</Th>
                          <Th>{t('inquiry.price')}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {previewRows.map((row) => (
                          <Tr key={row.key}>
                            <Td>{row.currencyCode}</Td>
                            <Td>{row.rowLabel}</Td>
                            <Td>{row.quantity}</Td>
                            <Td>
                              {row.price != null
                                ? `${row.price} ${row.currencyCode}`
                                : '—'}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

            <Button
              mt={4}
              variant="texas-solid"
              size="sm"
              isDisabled={selectedCurrencyIds.length === 0}
              isLoading={adding}
              onClick={handleAdd}
            >
              {t('general.add')}
            </Button>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}
